"use client";

import { ArrowUpRight, Bike, Bus, CarFront, TrainFront } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { MapContainer, TileLayer, FeatureGroup, Circle, Marker, Popup } from "react-leaflet";
import { Link } from "@/components/common/Link";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
const DefaultIcon = L.icon({
  iconUrl: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720548/img/marker-icon-red.webp",
  shadowUrl: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720548/img/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41]
});
L.Marker.prototype.options.icon = DefaultIcon;
export default function Location() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const transports = [{
    icon: TrainFront,
    label: "Logistica Tecnica · Hub Ferroviario",
    detail: "Collegamenti diretti con i principali snodi industriali per forniture pesanti"
  }, {
    icon: Bus,
    label: "Unità Mobili · Logistica su Gomma",
    detail: "Accesso facilitato per mezzi tecnici e attrezzature di bonifica"
  }, {
    icon: Bike,
    label: "Squadre Tecniche · Mobilità Rapida",
    detail: "Unità operative agili per interventi in centri storici e aree pedonali"
  }, {
    icon: CarFront,
    label: "Pronto Intervento · Rete Autostradale",
    detail: "Posizione strategica per garantire rapidità d'azione su tutto il territorio"
  }];
  return <section data-section-id="550" id="sedi-e-area-operativa" className="bg-background py-16 md:py-24 lg:py-32 text-foreground">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div ref={ref} className={`grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-10 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="lg:col-span-3 relative">
            <div className="relative overflow-hidden rounded-lg shadow-lg h-[420px] sm:h-[520px] lg:h-full min-h-[500px] bg-secondary">
              <MapContainer center={[46.4636, 11.1492]} zoom={7} scrollWheelZoom={false} className="absolute inset-0 w-full h-full grayscale-[40%] contrast-110 z-0">
                <TileLayer attribution='&copy; <Link to="https://www.openstreetmap.org/copyright">OpenStreetMap</Link> contributors' url="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720548/img/{y}.png" />
                <FeatureGroup>
                  <Circle center={[45.8, 10.8]} radius={120000} pathOptions={{
                  color: "#0066FF",
                  fillColor: "#0066FF",
                  fillOpacity: 0.3,
                  weight: 2
                }} />
                </FeatureGroup>
                <Marker position={[46.4636, 11.1492]}>
                  <Popup>
                    <div className="text-sm font-sans">
                      <strong className="block text-secondary">Aera Sanum HQ</strong>
                      Via della Zarga 61, 38015 Lavis (TN)
                    </div>
                  </Popup>
                </Marker>
              </MapContainer>

              <div className="pointer-events-none absolute inset-0 z-10" style={{
              background: "linear-gradient(135deg, hsla(174, 100%, 33%, 0.15) 0%, hsla(222, 47%, 7%, 0.25) 100%)"
            }} aria-hidden="true" />

              <div className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20">
                <div className="relative flex items-center justify-center">
                  <span className="absolute inline-flex h-16 w-16 rounded-lg bg-primary opacity-40 animate-ping" />
                  <span className="absolute inline-flex h-10 w-10 rounded-lg bg-primary opacity-60 animate-pulse" />
                  <span className="relative inline-flex h-6 w-6 rounded-lg bg-primary ring-4 ring-background shadow-lg" />
                </div>
                <div className="mt-3 flex justify-center">
                  <span className="inline-flex items-center gap-1.5 rounded-lg bg-foreground text-background px-3 py-1.5 text-xs font-semibold shadow-lg font-default tracking-wide">
                    <span className="inline-block h-1.5 w-1.5 rounded-lg bg-primary" />
                    AERA_SANUM
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2 bg-card rounded-lg p-7 sm:p-10 lg:p-12 flex flex-col border border-border">
            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-secondary font-default">
              Area Operativa
            </span>
            <h2 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-secondary font-default leading-[1.05]">
              Copertura
              <br />
              tecnica capillare.
            </h2>

            <p className="mt-5 text-base text-muted-foreground max-w-md">
              Sede centrale Aera. Garantiamo una copertura tecnica capillare e capacità di risposta rapida per la bonifica specialistica di impianti HVAC, condotti aria e sistemi di estrazione fumi professionali.
            </p>

            <div className="mt-8 flex-1 flex flex-col">
              {transports.map((t, i) => {
              const Icon = t.icon;
              return <div key={i} data-index={i}>
                    {i > 0 && <Separator className="bg-border/70" />}
                    <div className="flex items-start gap-5 py-5">
                      <div className="flex-shrink-0 flex h-12 w-12 items-center justify-center rounded-lg bg-secondary text-secondary-foreground">
                        <Icon className="h-5 w-5" strokeWidth={1.5} />
                      </div>
                      <div className="flex-1 min-w-0 pt-1">
                        <p className="text-base font-semibold text-secondary font-default">
                          {t.label}
                        </p>
                        <p className="mt-1 text-sm text-muted-foreground">
                          {t.detail}
                        </p>
                      </div>
                    </div>
                  </div>;
            })}
            </div>

            <div className="mt-6 rounded-lg border border-border bg-background/50 p-5">
              <p className="text-sm text-foreground">
                <span className="font-semibold font-default">Sopralluogo? </span>
                Richiedi una consulenza tecnica in loco.
              </p>
              <Button variant="link" className="mt-1 h-auto p-0 text-primary-foreground bg-transparent hover:bg-transparent" asChild>
                <Link to="/TechnicalInspectionPage">
                  <span className="inline-flex items-center gap-1 text-sm font-semibold text-foreground font-default group cursor-pointer">
                    Prenota sopralluogo
                    <span className="inline-flex h-5 w-5 items-center justify-center rounded-lg bg-primary text-primary-foreground transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5">
                      <ArrowUpRight className="h-3 w-3" strokeWidth={2.5} />
                    </span>
                  </span>
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}